import { filterStyle } from './storyHtml';

const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'DIV', 'BLOCKQUOTE']);
const TEXT_PROPS = ['font-family', 'font-size', 'color', 'font-weight', 'font-style', 'text-decoration'];
const PASTE_PROPS = new Set([
  'color', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
  'text-align', 'text-indent', 'line-height', 'margin', 'margin-left', 'margin-top', 'margin-bottom',
]);

const isWordHtml = (html) => /urn:schemas-microsoft-com|class="?Mso|mso-|<o:p/i.test(html);

const parseDecls = (text) => {
  const out = {};
  (text || '').split(';').forEach(d => {
    const i = d.indexOf(':');
    if (i > 0) out[d.slice(0, i).trim().toLowerCase()] = d.slice(i + 1).trim();
  });
  return out;
};

const toCss = (decls) => Object.entries(decls).map(([k, v]) => `${k}: ${v}`).join('; ');

// Close matches for common Word fonts a reader may not have (fonts the site loads come first)
const fontStack = (value) => {
  const low = value.toLowerCase();
  if (/eb garamond|open sans|courier prime/.test(low)) return value;
  const first = value.split(',')[0].trim();
  if (low.includes('garamond')) return `${first}, 'EB Garamond', serif`;
  if (/calibri|aptos|arial|helvetica|verdana|segoe|tahoma|trebuchet|century gothic|franklin/.test(low)) return `${first}, 'Open Sans', sans-serif`;
  if (/courier|consolas/.test(low)) return `${first}, 'Courier Prime', monospace`;
  return value;
};

// Word keeps the document's default font/size/spacing in a <style> block (e.g. p.MsoNormal) - collect those rules
const parseWordStyleRules = (doc) => {
  const rules = [];
  doc.querySelectorAll('style').forEach(el => {
    const css = el.textContent.replace(/<!--|-->/g, '').replace(/\/\*[\s\S]*?\*\//g, '');
    const re = /([^{}@]+)\{([^}]*)\}/g;
    let m;
    while ((m = re.exec(css))) {
      const decls = parseDecls(m[2]);
      m[1].split(',').map(s => s.trim()).forEach(sel => {
        const sm = sel.match(/^([a-z0-9]+)?(?:\.([\w-]+))?$/i);
        if (sm && (sm[1] || sm[2])) rules.push({ tag: sm[1]?.toUpperCase(), cls: sm[2], decls });
      });
    }
  });
  return rules;
};

const classDecls = (el, rules) => {
  const merged = {};
  const classes = (el.getAttribute('class') || '').split(/\s+/).filter(Boolean);
  rules.forEach(r => {
    const tagOk = !r.tag || r.tag === el.tagName;
    const clsOk = !r.cls || classes.includes(r.cls);
    if (tagOk && clsOk && (r.tag || r.cls)) Object.assign(merged, r.decls);
  });
  return merged;
};

const unwrap = (el) => {
  while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
  el.remove();
};

export const cleanPastedHtml = (html) => {
  if (!html || !isWordHtml(html)) return html;
  const prepared = html
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<!\[if[^>]*>|<!\[endif\]>/gi, '');
  const doc = new DOMParser().parseFromString(prepared, 'text/html');
  const rules = parseWordStyleRules(doc);
  doc.querySelectorAll('style, meta, link, title, script, xml').forEach(el => el.remove());
  Array.from(doc.getElementsByTagName('o:p')).forEach(unwrap);

  doc.body.querySelectorAll('*').forEach(el => {
    const inline = el.getAttribute('style') || '';
    if (/mso-list:\s*ignore/i.test(inline)) {
      el.textContent = el.textContent.replace(/[·\u00b7\uf0b7]/g, '•').replace(/\s+/g, ' ');
    }
    const tabs = inline.match(/mso-tab-count:\s*(\d+)/i);
    if (tabs) el.innerHTML = '&nbsp;'.repeat(8 * Number(tabs[1]));
    const decls = { ...classDecls(el, rules), ...parseDecls(inline) };
    const align = el.getAttribute('align');
    if (align && !decls['text-align']) decls['text-align'] = align.toLowerCase();
    if (decls['font-family'] && /symbol|wingdings/i.test(decls['font-family'])) delete decls['font-family'];
    if (decls['font-family']) decls['font-family'] = fontStack(decls['font-family']);
    // Word's automatic paragraph spacing ("auto") is 14pt
    ['top', 'bottom'].forEach(side => {
      if ((decls[`mso-margin-${side}-alt`] || '').toLowerCase() === 'auto' && !decls[`margin-${side}`]) decls[`margin-${side}`] = '14pt';
    });
    el.removeAttribute('align');
    el.removeAttribute('class');
    el.removeAttribute('lang');
    const css = filterStyle(toCss(decls), PASTE_PROPS);
    if (css) el.setAttribute('style', css); else el.removeAttribute('style');

    // Paragraph-level font settings must sit on the text itself for the editor to keep them
    if (BLOCK_TAGS.has(el.tagName) && el.childNodes.length) {
      const own = parseDecls(css);
      const textDecls = {};
      TEXT_PROPS.forEach(p => { if (own[p]) { textDecls[p] = own[p]; delete own[p]; } });
      if (Object.keys(textDecls).length) {
        const span = doc.createElement('span');
        span.setAttribute('style', toCss(textDecls));
        while (el.firstChild) span.appendChild(el.firstChild);
        el.appendChild(span);
        const rest = toCss(own);
        if (rest) el.setAttribute('style', rest); else el.removeAttribute('style');
      }
    }
  });
  return doc.body.innerHTML;
};
